import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon, KeyRoundIcon, ShieldCheckIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PIN_REGEX, PIN_REQUIRED_LENGTH } from '@/use-cases/set-pin';
import styles from './pin-fields.module.scss';

type PinFieldsProps = {
	pin: string;
	setPin: (newPin: string) => void;
	pinConfirmation: string;
	setPinConfirmation: (newPin: string) => void;
};

export const PinFields: React.FC<PinFieldsProps> = ({
	pin,
	setPin,
	pinConfirmation,
	setPinConfirmation,
}) => {
	const [isPinVisible, setIsPinVisible] = useState(false);
	const [pinTouched, setPinTouched] = useState(false);
	const [confirmationTouched, setConfirmationTouched] = useState(false);

	const hasValidFormat = PIN_REGEX.test(pin);
	const pinsMatch = pin === pinConfirmation;

	const showFormatError = pinTouched && pin !== '' && !hasValidFormat;
	const showMismatchError =
		confirmationTouched && pinConfirmation !== '' && !pinsMatch;

	return (
		<div className={styles['pin-fields']}>
			<p className={styles.explanation}>
				<ShieldCheckIcon size={18} aria-hidden="true" />
				<span>
					Este PIN es tu clave de firma: te lo pediremos cada vez que
					firmes un documento. No lo compartas con nadie, ni siquiera
					con personal de CyberSign.
				</span>
			</p>

			<p className={styles.notice}>
				<KeyRoundIcon size={18} aria-hidden="true" />
				<span>
					El PIN es único para tu cuenta, no para cada certificado. Si
					ya tienes certificados o emites otros más adelante, todos se
					firman con este mismo PIN, y lo que configures aquí
					reemplazará el PIN que tengas actualmente.
				</span>
			</p>

			<div className={styles.row}>
				<div className={styles.field}>
					<Label htmlFor="signing-pin">PIN de firma</Label>
					<div className={styles['input-with-toggle']}>
						<Input
							id="signing-pin"
							type={isPinVisible ? 'text' : 'password'}
							autoComplete="new-password"
							inputMode="text"
							maxLength={PIN_REQUIRED_LENGTH}
							placeholder="8 caracteres"
							value={pin}
							aria-describedby="signing-pin-hint"
							aria-invalid={showFormatError}
							onBlur={() => setPinTouched(true)}
							onChange={(event) =>
								setPin(
									event.target.value.slice(
										0,
										PIN_REQUIRED_LENGTH,
									),
								)
							}
						/>
						<button
							type="button"
							className={styles.toggle}
							onClick={() => setIsPinVisible((visible) => !visible)}
							aria-label={
								isPinVisible ? 'Ocultar PIN' : 'Mostrar PIN'
							}
							aria-pressed={isPinVisible}
						>
							{isPinVisible ? (
								<EyeOffIcon size={18} />
							) : (
								<EyeIcon size={18} />
							)}
						</button>
					</div>
				</div>

				<div className={styles.field}>
					<Label htmlFor="signing-pin-confirmation">
						Confirma tu PIN
					</Label>
					<Input
						id="signing-pin-confirmation"
						type={isPinVisible ? 'text' : 'password'}
						autoComplete="new-password"
						maxLength={PIN_REQUIRED_LENGTH}
						placeholder="Repite tu PIN"
						value={pinConfirmation}
						aria-invalid={showMismatchError}
						onBlur={() => setConfirmationTouched(true)}
						onChange={(event) =>
							setPinConfirmation(
								event.target.value.slice(
									0,
									PIN_REQUIRED_LENGTH,
								),
							)
						}
					/>
				</div>
			</div>

			<p
				id="signing-pin-hint"
				className={
					showFormatError ? styles.error : styles.hint
				}
			>
				Debe tener exactamente {PIN_REQUIRED_LENGTH} caracteres
				alfanuméricos, sin espacios ni símbolos.
			</p>

			{showMismatchError && (
				<p className={styles.error} role="alert">
					Los PIN no coinciden.
				</p>
			)}

			{hasValidFormat && pinsMatch && pinConfirmation !== '' && (
				<p className={styles.success}>PIN listo.</p>
			)}
		</div>
	);
};
